import React, { useEffect, useMemo, useState } from "react";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Container from "react-bootstrap/Container";
import logo from "assets/the-oven-delights-logo.png";
import { Link, useLocation } from "react-router-dom";
// Navbar.Collapse from RBv2 uses react-transition-group and can stay in
// "exited" (display:none) even when expanded=true. Use a plain div with
// Bootstrap classes instead so Bootstrap's own CSS controls visibility.
import {
  AiOutlineUser,
  AiOutlineLogin,
  AiOutlinePrinter,
  AiFillDashboard,
  AiOutlineAccountBook,
  AiFillAmazonSquare,
  AiOutlineCheckCircle,
  AiOutlineShoppingCart,
  AiOutlineCoffee,
  AiOutlineFire,
  AiOutlineTeam,
  AiOutlineShop,
  AiOutlineMenu,
  AiOutlineCar,
} from "react-icons/ai";
import { FiInbox, FiMonitor } from "react-icons/fi";
import NavDropdown from "react-bootstrap/NavDropdown";
import { SUPPORTED_LANGUAGES } from "utils/i18n";
import { cartCount, useCart } from "utils/cart";
import { useTranslation } from "react-i18next";
import { PERMISSIONS as P } from "common/permissions";
import {
  getAccountPath,
  getLoggedInUser,
  getPermissions,
  hasPermission,
} from "utils/auth";

const MD_BREAKPOINT = 768;

// Each item shows for users with one of its permissions (see
// common/permissions.js); `customer: true` items show for everyone logged in
// who isn't staff. `more: true` items go under "More" when someone has many
// items (the admin), so the bar stays on one line.
const navigationConfig = [
  {
    to: "/dashboard",
    text: "Dashboard",
    perms: [P.DASHBOARD],
    icon: <AiFillDashboard style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/quick-order",
    text: "Quick Order",
    perms: [P.QUICK_ORDER],
    icon: <AiOutlineUser style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/token",
    text: "Token",
    perms: [P.QUEUE_ADD],
    icon: <AiOutlinePrinter style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/queue",
    text: "Queue",
    perms: [P.QUEUE_ADD, P.QUEUE_STATUS],
    icon: <AiOutlineCoffee style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/bake",
    text: "Bake Board",
    perms: [P.BAKE_BOARD],
    icon: <AiOutlineFire style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/deliveries",
    text: "Deliveries",
    perms: [P.DELIVERY],
    more: true,
    icon: <AiOutlineCar style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/cash-drawer",
    text: "Cash drawer",
    perms: [P.DELIVERY],
    more: true,
    icon: <FiInbox style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/pay-display",
    text: "Customer screen",
    perms: [P.QUICK_ORDER, P.DELIVERY],
    more: true,
    icon: <FiMonitor style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/order",
    more: true,
    text: "Order",
    perms: [P.QUICK_ORDER],
    icon: <AiOutlineAccountBook style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/product",
    more: true,
    text: "Product",
    perms: [P.PRODUCTS],
    icon: <AiFillAmazonSquare style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/order-checker",
    more: true,
    text: "Order Checker",
    perms: [P.ORDER_CHECK],
    icon: <AiOutlineCheckCircle style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/users",
    more: true,
    text: "Users",
    perms: [P.USERS],
    icon: <AiOutlineTeam style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/online-orders",
    text: "Online Orders",
    perms: [P.QUICK_ORDER, P.DELIVERY],
    icon: <AiOutlineShoppingCart style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/buy-cake",
    text: "nav.order",
    customer: true,
    icon: <AiOutlineShop style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/cart",
    text: "nav.cart",
    customer: true,
    cart: true,
    icon: <AiOutlineShoppingCart style={{ marginBottom: "2px" }} />,
  },
];

const LANGUAGE_NAMES = { en: "English", hi: "हिन्दी", "hi-Latn": "Hinglish" };

function NavBar({ currentPathName, onLogout, isLoggedIn, userLevel }) {
  const { pathname } = useLocation();
  const isHomePage = pathname === "/home";
  const [navColour, updateNavbar] = useState(false);
  // Separate desktop detection from the mobile open/close toggle so we never
  // accidentally unmount the Collapse on desktop (RBv2 unmounts when expanded=false).
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= MD_BREAKPOINT,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { t: ts } = useTranslation("shop");
  const cartLines = useCart();
  const isStaff = getPermissions().length > 0;
  // first name, next to the logo
  const helloName = ((getLoggedInUser() || {}).name || "")
    .trim()
    .split(/\s+/)[0];

  useEffect(() => {
    function onScroll() {
      updateNavbar(window.scrollY >= 20);
    }
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function onResize() {
      setIsDesktop(window.innerWidth >= MD_BREAKPOINT);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Desktop: always keep Collapse mounted (expanded=true).
  // Mobile: controlled by the hamburger toggle.
  const navExpanded = isDesktop || mobileOpen;

  const filteredNavigation = useMemo(() => {
    // visitors (shop and cart pages) get the customer items
    if (!isLoggedIn) return navigationConfig.filter((item) => item.customer);
    const staff = getPermissions().length > 0;
    return navigationConfig.filter((item) =>
      item.customer ? !staff : hasPermission(...item.perms),
    );
    // userLevel changes when the user is reloaded from the server
  }, [isLoggedIn, userLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  function closeOnMobile() {
    setMobileOpen(false);
  }

  // Many items (the admin): the less used ones go under "More".
  const compact = filteredNavigation.length > 6;
  const barItems = compact
    ? filteredNavigation.filter((i) => !i.more)
    : filteredNavigation;
  const moreItems = compact ? filteredNavigation.filter((i) => i.more) : [];
  const isActive = (to) => pathname === to;

  // Profile: the customer's pages, language, password, log out. Staff with a
  // "More" menu get these at the bottom of it instead of a separate menu.
  const profileItems = () => (
    <>
      {!isStaff && (
        <>
          <NavDropdown.Item as={Link} to="/orders" onClick={closeOnMobile}>
            {ts("nav.myOrders")}
          </NavDropdown.Item>
          <NavDropdown.Item
            as={Link}
            to="/my?tab=tokens"
            onClick={closeOnMobile}
          >
            {ts("nav.myTokens")}
          </NavDropdown.Item>
          <NavDropdown.Item
            as={Link}
            to="/my?tab=addresses"
            onClick={closeOnMobile}
          >
            {ts("nav.addresses")}
          </NavDropdown.Item>
          <NavDropdown.Divider />
        </>
      )}
      <NavDropdown.Header>{ts("nav.language")}</NavDropdown.Header>
      {SUPPORTED_LANGUAGES.map((lng) => (
        <NavDropdown.Item
          key={lng}
          active={i18n.language === lng}
          onClick={() => i18n.changeLanguage(lng)}
        >
          {LANGUAGE_NAMES[lng] || lng}
        </NavDropdown.Item>
      ))}
      <NavDropdown.Divider />
      <NavDropdown.Item as={Link} to="/my?tab=password" onClick={closeOnMobile}>
        {ts("nav.changePassword")}
      </NavDropdown.Item>
      <NavDropdown.Item
        as={Link}
        to="/login"
        onClick={() => {
          closeOnMobile();
          onLogout();
        }}
      >
        {ts("nav.logout")}
      </NavDropdown.Item>
    </>
  );

  return (
    <Navbar
      expanded={navExpanded}
      onToggle={setMobileOpen}
      expand="md"
      fixed="top"
      className={navColour ? "sticky" : "navbar"}
    >
      <Container>
        <Navbar.Brand href="/home" className="d-flex align-items-center">
          <img src={logo} className="logo" alt="brand" />
          {isLoggedIn && helloName && (
            <span className="navHello">
              {ts("my.hello", { name: helloName })}
            </span>
          )}
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="responsive-navbar-nav">
          <span></span>
          <span></span>
          <span></span>
        </Navbar.Toggle>

        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav
            className={`ms-auto ${isStaff ? "navCompact" : ""}`}
            defaultActiveKey="#home"
          >
            {isHomePage && isLoggedIn && (
              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to={getAccountPath()}
                  onClick={closeOnMobile}
                >
                  <div className="navLink">
                    <AiFillDashboard style={{ marginBottom: "2px" }} />
                    {getPermissions().length > 0
                      ? t("Dashboard")
                      : t("My Orders")}
                  </div>
                </Nav.Link>
              </Nav.Item>
            )}
            {barItems.map((item) => (
              <Nav.Item key={item.text}>
                <Nav.Link
                  as={Link}
                  to={item.to}
                  onClick={closeOnMobile}
                  className={isActive(item.to) ? "navActive" : ""}
                  title={item.customer ? ts(item.text) : t(item.text)}
                >
                  <div className="navLink">
                    {item.icon}{" "}
                    <span className="navText">
                      {item.customer ? ts(item.text) : t(item.text)}
                    </span>
                    {item.cart && cartCount(cartLines) > 0 && (
                      <span className="navCartBadge">
                        {cartCount(cartLines)}
                      </span>
                    )}
                  </div>
                </Nav.Link>
              </Nav.Item>
            ))}

            {moreItems.length > 0 && (
              <NavDropdown
                align="end"
                className={`navProfile ${moreItems.some((i) => isActive(i.to)) ? "navActive" : ""}`}
                title={
                  <span className="navLink">
                    <AiOutlineMenu style={{ marginBottom: "2px" }} />
                    <span className="navText">{t("More")}</span>
                  </span>
                }
              >
                {moreItems.map((item) => (
                  <NavDropdown.Item
                    key={item.text}
                    as={Link}
                    to={item.to}
                    active={isActive(item.to)}
                    onClick={closeOnMobile}
                  >
                    {item.icon} {t(item.text)}
                  </NavDropdown.Item>
                ))}
                {isLoggedIn && (
                  <>
                    <NavDropdown.Divider />
                    {profileItems()}
                  </>
                )}
              </NavDropdown>
            )}
            {isLoggedIn && moreItems.length === 0 && (
              <NavDropdown
                align="end"
                className="navProfile"
                title={
                  <span className="navLink">
                    <AiOutlineUser style={{ marginBottom: "2px" }} />
                    <span className="navText">{ts("nav.profile")}</span>
                  </span>
                }
              >
                {profileItems()}
              </NavDropdown>
            )}
            {!isLoggedIn && currentPathName && (
              <Nav.Item>
                <Nav.Link as={Link} to="/login" onClick={closeOnMobile}>
                  <div className="navLink">
                    <AiOutlineLogin style={{ marginBottom: "2px" }} />
                    {t("Login")}
                  </div>
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default NavBar;
